import { defineStore } from "pinia";
import JSONAPISerializer from "json-api-serializer";
import type { JSONAPIDocument } from "json-api-serializer";
import type { AxiosRequestConfig } from "axios";
import axios, { type CancelTokenSource } from "axios";
import { ref } from "vue";
import Trans from "@/i18n/translation";
import type { QueryFilter } from "@/types/object_classes/QueryFilter";
import { showError } from "@/services/ShowMessages";
import type { JsonApiPagination } from "@/types/object_classes/JsonApiPagination";
import type { ApiCallOptions } from "@/types/object_classes/ApiCallOptions";
import { useAuthStore } from "@/stores/auth";
import { activeFlowId, activeFlowName, activeFlowStep, adoptSeq, describeError, nextRequestSeq, normalizeAttributes } from "@/services/observability/FlowLogger";
import * as Sentry from "@sentry/vue";

export const useApiStore = defineStore("api", () => {
  const authStore = useAuthStore();
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const PARALLEL_REQUESTS = import.meta.env.VITE_PARALLEL_REQUESTS;
  let CURRENT_LANGUAGE = localStorage.getItem("user-locale");
  if (CURRENT_LANGUAGE === null) {
    CURRENT_LANGUAGE = "en";
    const newLang = Trans.switchLanguage("en");
  }
  const CancelToken = ref(axios.CancelToken);
  const newLang = Trans.switchLanguage(CURRENT_LANGUAGE);

  const configApi: AxiosRequestConfig = {
    baseURL: `${BASE_URL}/api/v1`,
    headers: {
      "Content-Type": "application/vnd.api+json",
      Accept: "application/vnd.api+json",
    },
  };

  const axiosApi = ref(axios.create(configApi));

  const configAuth: AxiosRequestConfig = {
    baseURL: `${BASE_URL}/api`,
    headers: {
      "Content-Type": "application/vnd.api+json",
      Accept: "application/vnd.api+json",
    },
  };

  const axiosAuth = ref(axios.create(configAuth));

  const reportRequestFailure = (error: any): void => {
    if (error?.code === "ERR_CANCELED") {
      return;
    }

    Sentry.logger.error("api.request_failed", normalizeAttributes(describeError(error)));

    if (error?.response === undefined) {
      Sentry.captureException(error);
    }
  };

  const attachFlowHeaders = (config: any): any => {
    const flowId = activeFlowId();

    if (flowId !== null) {
      config.headers["X-Sentry-Flow-Id"] = flowId;
      config.headers["X-Sentry-Flow-Name"] = activeFlowName();

      const step = activeFlowStep();
      if (step !== null) {
        config.headers["X-Sentry-Flow-Step"] = step;
      }

      config.headers["X-Sentry-Flow-Seq"] = String(nextRequestSeq());
    }

    return config;
  };

  const attachBranchHeader = (config: any): any => {
    const branchId = localStorage.getItem("current-branch-id");

    if (branchId !== null) {
      config.headers["X-Branch-Id"] = branchId;
    }

    return config;
  };

  const adoptFlowSeqFromHeaders = (headers: any): void => {
    const value = headers?.["x-sentry-flow-seq"];
    if (typeof value === "string") {
      adoptSeq(Number(value));
    }
  };

  axiosApi.value.interceptors.request.use(attachFlowHeaders);
  axiosAuth.value.interceptors.request.use(attachFlowHeaders);
  axiosApi.value.interceptors.request.use(attachBranchHeader);
  axiosAuth.value.interceptors.request.use(attachBranchHeader);

  axiosApi.value.interceptors.response.use((response) => {
    adoptFlowSeqFromHeaders(response.headers);
    return response;
  }, (error) => {
    adoptFlowSeqFromHeaders(error?.response?.headers);
    reportRequestFailure(error);
    return Promise.reject(error);
  });

  axiosAuth.value.interceptors.response.use((response) => {
    adoptFlowSeqFromHeaders(response.headers);
    return response;
  }, (error) => {
    adoptFlowSeqFromHeaders(error?.response?.headers);
    reportRequestFailure(error);
    return Promise.reject(error);
  });

  const Serializer = new JSONAPISerializer();

  const meta = ref<Object | undefined>();

  const json_api_types = [
    {
      type: "bussiness-lines",
      schema: {
        relationships: {
          clientPermissionPages: {
            type: "client-permission-pages",
          },
        },
      },
    },
    {
      type: "branches",
      schema: {
        relationships: {
          businessLines: {
            type: "bussiness-lines",
          },
        },
      },
    },
    {
      type: "client-permission-pages",
      schema: {
        relationships: {
          forms: {
            type: "forms",
          },
          listings: {
            type: "listings",
          },
          actions: {
            type: "actions",
          },
          pageFilters: {
            type: "page-filters",
          },
        },
      },
    },
    { type: "client-permissions", schema: {} },
    { type: "login-as-permissions", schema: {} },
    {
      type: "api-sources",
      schema: {
        relationships: {
          apiStatuses: {
            type: "api-statuses",
          },
        },
      },
    },
    {
      type: "api-statuses",
      schema: {
        relationships: {
          apiSource: {
            type: "api-sources",
          },
          appStatuses: {
            type: "app-statuses",
          },
          apiSubstatuses: {
            type: "api-substatuses",
          },
        },
      },
    },
    {
      type: "api-substatuses",
      schema: {
        relationships: {
          apiStatus: {
            type: "api-statuses",
          },
        },
      },
    },
    { type: "activities", schema: {} },
    {
      type: "contacts",
      schema: {
        relationships: {
          contactNotes: {
            type: "contact-notes",
          },
          companyNotes: {
            type: "company-notes",
          },
          contactFiles: {
            type: "contact-files",
          },
          company: {
            type: "companies",
          },
        },
      },
    },
    {
      type: "contact-notes",
      schema: {
        relationships: {
          creatingUser: {
            type: "users",
          },
          updatingUser: {
            type: "users",
          },
          contactNotes: {
            type: "contact-notes",
          },
        },
      },
    },
    { type: "contact-files", schema: {} },
    {
      type: "company-notes",
      schema: {
        relationships: {
          creatingUser: {
            type: "users",
          },
          updatingUser: {
            type: "users",
          },
          contacts: {
            type: "contacts",
          },
        },
      },
    },
    { type: "company-files", schema: {} },
    {
      type: "charts",
      schema: {
        relationships: {
          chartDashboards: {
            type: "chart-dashboards",
          },
        },
      },
    },
    {
      type: "chart-dashboards",
      schema: {
        relationships: {
          charts: {
            type: "charts",
          },
        },
      },
    },
    { type: "chart-chart-dashboards", schema: {} },
    {
      type: "client-roles",
      schema: {
        relationships: {
          clientPermissions: {
            type: "client-permissions",
          },
        },
      },
    },
    {
      type: "login-as-roles",
      schema: {
        relationships: {
          loginAsPermissions: {
            type: "login-as-permissions",
          },
        },
      },
    },
    {
      type: "klg-permission-pages",
      schema: {
        relationships: {
          forms: {
            type: "forms",
          },
          listings: {
            type: "listings",
          },
          actions: {
            type: "actions",
          },
          pageFilters: {
            type: "page-filters",
          },
        },
      },
    },
    {
      type: "roles",
      schema: {
        relationships: {
          permissions: {
            type: "permissions",
          },
        },
      },
    },
    {
      type: "forms",
      schema: {
        relationships: {
          klgPermissionPages: {
            type: "klg-permission-pages",
          },
          formFields: {
            type: "form-fields",
          },
          clientPermissionPages: {
            type: "client-permission-pages",
          },
        },
      },
    },
    { type: "form-fields", schema: {} },
    {
      type: "listings",
      schema: {
        relationships: {
          klgPermissionPages: {
            type: "klg-permission-pages",
          },
          clientPermissionPages: {
            type: "client-permission-pages",
          },
          forms: {
            type: "forms",
          },
          listingFilters: {
            type: "listing-filters",
          },
          listingActions: {
            type: "listing-actions",
          },
        },
      },
    },
    { type: "page-filters", schema: {} },
    { type: "listing-filters", schema: {} },
    { type: "listing-actions", schema: {} },
    { type: "actions", schema: {} },
    {
      type: "users",
      schema: {
        relationships: {
          roles: {
            type: "roles",
          },
          loginAsRoles: {
            type: "loginAsRoles",
          },
          salesManager: {
            type: "sales-persons-purchasers",
          },
          keyAccountManager: {
            type: "option-values",
          },
          tmsOwner: {
            type: "option-values",
          },
          contractManagers: {
            type: "companies",
          },
          userSessions: {
            type: "user-sessions",
          },
        },
      },
    },
    {
      type: "client-users",
      schema: {
        relationships: {
          clientRoles: {
            type: "client-roles",
          },
          company: {
            type: "companies",
          },
        },
      },
    },
    { type: "client-user-permissions", schema: {} },
    { type: "user-permissions", schema: {} },
    { type: "permissions", schema: {} },
    { type: "marketing-ratios", schema: {} },
    {
      type: "user-impersonate-tokens",
      schema: {
        blacklist: ["id", "token", "code"],
      },
    },
    { type: "option-categories", schema: {} },
    { type: "option-values", schema: {} },
    {
      type: "tooltip-categories",
      schema: {
        relationships: {
          tooltipValues: {
            type: "tooltip-values",
          },
        },
      },
    },
    {
      type: "tooltip-values",
      schema: {
        relationships: {
          pageFields: {
            type: "page-fields",
          },
        },
      },
    },
    { type: "countries", schema: {} },
    { type: "counties", schema: {} },
    { type: "localities", schema: {} },
    {
      type: "companies",
      schema: {
        relationships: {
          bussinessLines: {
            type: "bussiness-lines",
          },
          companyBillingMethods: {
            type: "company-billing-methods",
          },
          tmsRoles: {
            type: "tms-roles",
          },
          companyFeatures: {
            type: "company-features",
          },
          companyNotes: {
            type: "company-notes",
          },
          contacts: {
            type: "contacts",
          },
          clientUsers: {
            type: "client-users",
          },
          companyFiles: {
            type: "company-files",
          },
          companyBusinessLines: {
            type: "company-business-lines",
          },
          userToCompanies: {
            type: "user-to-companies",
          },
          userToBusinessLines: {
            type: "user-to-business-lines",
          },
          internationalRoadOrderEntry: {
            type: "international-road-order-entries",
          },
          domesticRoadOrderEntry: {
            type: "domestic-road-order-entries",
          },
          strongoOrderEntry: {
            type: "strongo-order-entries",
          },
          companyStrongoSetting: {
            type: "company-strongo-settings",
          },
          orderEntryLeadtimes: {
            type: "order-entry-leadtimes",
          },
          strongoLeadtimes: {
            type: "strongo-order-entry-leadtimes",
          },
          strongoDepartments: {
            type: "strongo-departments",
          },
          orderEntryEmails: {
            type: "order-entry-emails",
          },
          companyContracts: {
            type: "company-contracts",
          },
          contractManagers: {
            type: "users",
          },
          salesPerson: {
            type: "users",
          },
          parentAccount: {
            type: "companies",
          },
          accountReceivable: {
            type: "account-receivables",
          },
          lastCustomerLedgerEntry: {
            type: "customer-ledger-entries",
          },
          companyBusinessLineDetails: {
            type: "company-business-line-details",
          },
          companyAnnexes: {
            type: "company-annexes",
          },
          activeCompanyAnnexes: {
            type: "company-annexes",
          },
          tmsRoadCreationDate: {
            type: "tms-road-data",
          },
          tmsSeaCreationDate: {
            type: "tms-sea-data",
          },
          erpCreationDate: {
            type: "erp-data",
          },
          strongoCreationDate: {
            type: "strongo-data",
          },
          lastPaymentNote: {
            type: "copmpany-notes",
          },
        },
        blacklist: ["tms_road_unique_code", "status", "tms_road_api_status"],
      },
    },
    { type: "tms-roles", schema: {} },
    { type: "company-tms-roles", schema: {} },
    { type: "company-billing-methods", schema: {} },
    { type: "company-roles", schema: {} },
    { type: "company-features", schema: {} },
    { type: "activity-logs", schema: {} },
    {
      type: "inbounds",
      schema: {
        relationships: {
          company: {
            type: "companies",
          },
          apiStatus: {
            type: "api-statuses",
          },
        },
      },
    },
    {
      type: "app-statuses",
      schema: {
        relationships: {
          apiStatuses: {
            type: "api-statuses",
          },
        },
      },
    },
    {
      type: "outbounds",
      schema: {
        relationships: {
          company: {
            type: "companies",
          },
          carrier: {
            type: "shippers",
          },
          apiStatus: {
            type: "api-statuses",
          },
        },
      },
    },
    {
      type: "domestic-shipments",
      schema: {
        relationships: {
          domesticPod: {
            type: "domestic-pods",
          },
          invoiceLines: {
            type: "invoice-lines",
          },
          orderData: {
            type: "order-datas",
          },
          transportOrder: {
            type: "domestic-road-transport-orders",
          },
          shipmentGoods: {
            type: "domestic-road-shipment-goods",
          },
          shipmentActivities: {
            type: "domestic-road-shipment-activities",
          },
          domesticRoadShipmentTrackingUrl: {
            type: "domestic-road-shipment-tracking-urls",
          },
          company: {
            type: "companies",
          },
          apiStatus: {
            type: "api-statuses",
          },
        },
      },
    },
    {
      type: "international-shipments",
      schema: {
        relationships: {
          internationalPod: {
            type: "international-pods",
          },
          invoiceLines: {
            type: "invoice-lines",
          },
          orderData: {
            type: "order-datas",
          },
          transportOrder: {
            type: "international-road-transport-orders",
          },
          shipmentGoods: {
            type: "international-road-shipment-goods",
          },
          shipmentActivities: {
            type: "international-road-shipment-activities",
          },
          internationalRoadShipmentTrackingUrl: {
            type: "international-road-shipment-tracking-urls",
          },
          company: {
            type: "companies",
          },
          apiStatus: {
            type: "api-statuses",
          },
        },
      },
    },
    {
      type: "tms-sea-shipments",
      schema: {
        relationships: {
          seaInvoiceShipments: {
            type: "sea-invoice-shipments",
          },
          seaShipmentContainers: {
            type: "sea-shipment-containers",
          },
          company: {
            type: "companies",
          },
          apiStatus: {
            type: "api-statuses",
          },
        },
      },
    },
    {
      type: "sea-shipment-containers",
      schema: {
        relationships: {
          routings: {
            type: "routings",
          },
          freightSellEsts: {
            type: "freight-sell-ests",
          },
          seaAirDocuments: {
            type: "sea-air-documents",
          },
          apiStatus: {
            type: "api-statuses",
          },
        },
      },
    },
    {
      type: "tms-air-shipments",
      schema: {
        relationships: {
          routings: {
            type: "routings",
          },
          seaAirDocuments: {
            type: "sea-air-documents",
          },
          airInvoiceShipments: {
            type: "sea-invoice-shipments",
          },
          company: {
            type: "companies",
          },
          apiStatus: {
            type: "api-statuses",
          },
        },
      },
    },
    { type: "routings", schema: {} },
    { type: "freight-sell-ests", schema: {} },
    { type: "sea-air-documents", schema: {} },
    {
      type: "vbs-report-outbounds",
      schema: {
        relationships: {
          company: {
            type: "companies",
          },
          carrier: {
            type: "shippers",
          },
          apiStatus: {
            type: "api-statuses",
          },
        },
      },
    },
    {
      type: "vbs-report-inbounds",
      schema: {
        relationships: {
          company: {
            type: "companies",
          },
          apiStatus: {
            type: "api-statuses",
          },
        },
      },
    },
    { type: "authorized-emails", schema: {} },
    { type: "api-import-results", schema: {} },
    {
      type: "customer-ledger-entries",
      schema: {
        relationships: {
          customerLedgerGlEntries: {
            type: "customer-ledger-gl-entries",
          },
          invoice: {
            type: "invoices",
          },
          seaInvoice: {
            type: "sea-invoices",
          },
          strongoInvoices: {
            type: "strongo-invoices",
          },
          customerLedgerEntryReversal: {
            type: "customer-ledger-entry-reversals",
          },
          customerLedgerEntryOriginal: {
            type: "customer-ledger-entry-reversals",
          },
          invoicedAwbs: {
            type: "strongo-invoices",
          },
          strongoInvoiceHistories: {
            type: "strongo-invoice-histories",
          },
          company: {
            type: "companies",
          },
        },
      },
    },
    { type: "customer-ledger-gl-entries", schema: {} },
    { type: "vendor-ledger-entries", schema: {} },
    { type: "customers", schema: {} },
    { type: "vendors", schema: {} },
    { type: "customer-posting-groups", schema: {} },
    { type: "vendor-posting-groups", schema: {} },
    {
      type: "sales-persons-purchasers",
      schema: {
        relationships: {
          user: {
            type: "users",
          },
        },
      },
    },
    { type: "reminder-terms", schema: {} },
    { type: "payment-terms", schema: {} },
    { type: "currencies", schema: {} },
    { type: "notifications", schema: {} },
    { type: "alerts", schema: {} },
    { type: "gen-business-posting-groups", schema: {} },
    { type: "vat-business-posting-groups", schema: {} },
    {
      type: "departments",
      schema: {
        relationships: {
          notifications: {
            type: "notifications",
          },
        },
      },
    },
    { type: "ad-departments", schema: {} },
    { type: "cleo-response-limits", schema: {} },
    { type: "client-cleo-response-limits", schema: {} },
    {
      type: "cleo-analysis-runs",
      schema: {
        relationships: {
          findings: { type: "cleo-analysis-findings" },
        },
      },
    },
    {
      type: "cleo-analysis-findings",
      schema: {
        relationships: {
          run: { type: "cleo-analysis-runs" },
        },
      },
    },
    {
      type: "cleo-documents",
      schema: {
        relationships: {
          createdByUser: { type: "users" },
        },
      },
    },
    {
      type: "json-resources",
      schema: {
        relationships: {
          jsonResourceFields: {
            type: "json-resource-fields",
          },
          forms: {
            type: "forms",
          },
        },
      },
    },
    { type: "json-resource-fields", schema: {} },
    { type: "vbs-shippers", schema: {} },
    { type: "invoices", schema: {} },
    {
      type: "invoice-lines",
      schema: {
        relationships: {
          domesticShipment: {
            type: "domestic-shipments",
          },
          internationalShipment: {
            type: "international-shipments",
          },
        },
      },
    },
    { type: "domestic-pods", schema: {} },
    { type: "table-column-changes", schema: {} },
    { type: "international-pods", schema: {} },
    { type: "erp-invoice-line-names", schema: {} },
    { type: "company-emails", schema: {} },
    {
      type: "company-business-lines",
      schema: {
        relationships: {
          companyEmail: {
            type: "company-emails",
          },
          clientEmail: {
            type: "company-emails",
          },
          companyKpis: {
            type: "company-kpis",
          },
          activeCompanyAnnexes: {
            type: "company-annexes",
          },
        },
      },
    },
    {
      type: "user-to-companies",
      schema: {
        relationships: {
          users: {
            type: "users",
          },
          companies: {
            type: "companies",
          },
          businessLines: {
            type: "bussiness-lines",
          },
        },
      },
    },
    {
      type: "user-to-business-lines",
      schema: {
        relationships: {
          users: {
            type: "users",
          },
          businessLines: {
            type: "bussiness-lines",
          },
        },
      },
    },
    {
      type: "incoterms",
      schema: {
        blacklist: ["display_name"],
      },
    },
    {
      type: "packages",
      schema: {
        blacklist: ["display_name"],
        relationships: {
          businessLines: {
            type: "bussiness-lines",
          },
        },
      },
    },
    {
      type: "order-entry-features",
      schema: {
        blacklist: ["display_name"],
        relationships: {
          businessLines: {
            type: "bussiness-lines",
          },
        },
      },
    },
    {
      type: "order-entry-emails",
      schema: {
        relationships: {
          companies: {
            type: "companies",
          },
        },
      },
    },
    {
      type: "domestic-order-entry-general-settings",
      schema: {
        relationships: {
          domesticOrderEntryResponsibles: {
            type: "users",
          },
        },
      },
    },
    {
      type: "international-order-entry-general-settings",
      schema: {
        relationships: {
          internationalOrderEntryResponsibles: {
            type: "users",
          },
        },
      },
    },
    {
      type: "strongo-order-entry-general-settings",
      schema: {
        relationships: {
          strongoOrderEntryResponsibles: {
            type: "users",
          },
        },
      },
    },
    { type: "domestic-order-entry-responsibles", schema: {} },
    { type: "international-order-entry-responsibles", schema: {} },
    { type: "strongo-order-entry-responsibles", schema: {} },
    {
      type: "international-road-order-entries",
      schema: {
        relationships: {
          incoterms: {
            type: "incoterms",
          },
          orderEntryFeatures: {
            type: "order-entry-features",
          },
          packages: {
            type: "packages",
          },
        },
      },
    },
    {
      type: "domestic-road-order-entries",
      schema: {
        relationships: {
          orderEntryFeatures: {
            type: "order-entry-features",
          },
          packages: {
            type: "packages",
          },
        },
      },
    },
    {
      type: "company-strongo-settings",
      schema: {},
    },
    {
      type: "strongo-order-entries",
      schema: {
        relationships: {
          orderEntryFeatures: {
            type: "order-entry-features",
          },
          packages: {
            type: "packages",
          },
        },
      },
    },
    {
      type: "order-entry-leadtimes",
      schema: {
        blacklist: ["guid", "warning"],
      },
    },
    {
      type: "strongo-order-entry-leadtimes",
      schema: {
        blacklist: ["guid", "warning"],
      },
    },
    { type: "strongo-departments", schema: {} },
    { type: "order-entry-addresses", schema: {} },
    {
      type: "sea-invoices",
      schema: {
        relationships: {
          seaInvoiceShipments: {
            type: "sea-invoice-shipments",
          },
          airInvoiceShipments: {
            type: "sea-invoice-shipments",
          },
          customerLedgerEntry: {
            type: "customer-ledger-entries",
          },
        },
      },
    },
    {
      type: "sea-invoice-shipments",
      schema: {
        relationships: {
          seaInvoiceShipments: {
            type: "sea-invoice-shipments",
          },
          airInvoiceShipments: {
            type: "sea-invoice-shipments",
          },
          customerLedgerEntry: {
            type: "customer-ledger-entries",
          },
        },
      },
    },
    { type: "payment-methods", schema: {} },
    {
      type: "legal-holidays",
      schema: {
        relationships: {
          country: {
            type: "countries",
          },
        },
      },
    },
    { type: "shippers", schema: {} },
    { type: "webmail-domains", schema: {} },
    { type: "domestic-road-transport-orders", schema: {} },
    { type: "domestic-road-transport-order-order-entry-features", schema: {} },
    { type: "strongo-transport-orders", schema: {} },
    { type: "order-entry-feature-strongo-transport-orders", schema: {} },
    {
      type: "ai-agents",
      schema: {
        relationships: {
          aiAgentPermissions: {
            type: "ai-agent-permissions",
          },
          aiAgentConversations: {
            type: "ai-agent-conversations",
          },
        },
      },
    },
    { type: "international-road-transport-orders", schema: {} },
    {
      type: "international-road-transport-order-order-entry-features",
      schema: {},
    },
    {
      type: "maintenances",
      schema: {
        relationships: {
          notificationInteraction: {
            type: "notification-interactions",
          },
        },
      },
    },
    { type: "maintenance-ips", schema: {} },
    {
      type: "site-notifications",
      schema: {
        relationships: {
          notificationInteraction: {
            type: "notification-interactions",
          },
        },
      },
    },
    {
      type: "company-contracts",
      schema: {
        relationships: {
          companyContractAddendums: {
            type: "company-contract-addendums",
          },
          activeCompanyContractAddendums: {
            type: "company-contract-addendums",
          },
          companyAnnexes: {
            type: "company-annexes",
          },
          activeCompanyAnnexes: {
            type: "company-annexes",
          },
        },
      },
    },
    {
      type: "releases",
      schema: {
        relationships: {
          notificationInteraction: {
            type: "notification-interactions",
          },
        },
      },
    },
    {
      type: "pages",
      schema: {
        relationships: {
          pageFields: {
            type: "page-fields",
          },
        },
      },
    },
    {
      type: "api-resources",
      schema: {
        relationships: {
          apiResourceFields: {
            type: "api-resource-fields",
          },
          apiRoutes: {
            type: "api-routes",
          },
        },
      },
    },
    {
      type: "api-resource-fields",
      schema: {
        relationships: {
          pageFields: {
            type: "page-fields",
          },
          apiResource: {
            type: "api-resources",
          },
          apiRoutes: {
            type: "api-routes",
          },
          apiRouteFields: {
            type: "api-routes",
          },
        },
      },
    },
    {
      type: "api-routes",
      schema: {
        relationships: {
          postApiRouteFields: {
            type: "api-route-fields",
          },
          responseApiRouteFields: {
            type: "api-route-fields",
          },
          metaResponseApiRouteFields: {
            type: "api-route-fields",
          },
          pathApiRouteFields: {
            type: "api-route-fields",
          },
          queryParameterApiRouteFields: {
            type: "api-route-fields",
          },
          filterApiRouteFields: {
            type: "api-route-fields",
          },
          apiRouteFields: {
            type: "api-route-fields",
          },
          apiResource: {
            type: "api-resources",
          },
          apiRouteGroup: {
            type: "api-route-groups",
          },
        },
      },
    },
    { type: "key-account-managers", schema: {} },
    { type: "user-settings", schema: {} },
    {
      type: "page-fields",
      schema: {
        relationships: {
          apiResourceFields: {
            type: "api-resource-fields",
          },
          tooltipValue: {
            type: "tooltip-values",
          },
        },
      },
    },
    {
      type: "api-route-groups",
      schema: {
        relationships: {
          apiRoutes: {
            type: "api-routes",
          },
        },
      },
    },
    {
      type: "application-api-resources",
      schema: {
        relationships: {
          applicationApiResourceFields: {
            type: "application-api-resource-fields",
          },
          applicationApiRoutes: {
            type: "application-api-routes",
          },
        },
      },
    },
    {
      type: "application-api-resource-fields",
      schema: {
        relationships: {
          applicationApiResource: {
            type: "application-api-resources",
          },
        },
      },
    },
    {
      type: "application-api-route-groups",
      schema: {
        relationships: {
          applicationApiRoutes: {
            type: "application-api-routes",
          },
        },
      },
    },
    {
      type: "application-api-routes",
      schema: {
        relationships: {
          applicationApiResource: {
            type: "application-api-resources",
          },
          applicationApiRouteGroup: {
            type: "application-api-route-groups",
          },
          postApiRouteFields: {
            type: "application-api-route-fields",
          },
          responseApiRouteFields: {
            type: "application-api-route-fields",
          },
          metaResponseApiRouteFields: {
            type: "application-api-route-fields",
          },
          pathApiRouteFields: {
            type: "application-api-route-fields",
          },
          queryParameterApiRouteFields: {
            type: "application-api-route-fields",
          },
          filterApiRouteFields: {
            type: "application-api-route-fields",
          },
          applicationApiRouteFields: {
            type: "application-api-route-fields",
          },
        },
      },
    },
    {
      type: "application-api-route-fields",
      schema: {
        relationships: {
          applicationApiRoute: {
            type: "application-api-routes",
          },
          applicationApiResourceField: {
            type: "application-api-resource-fields",
          },
        },
      },
    },
    {
      type: "domestic-road-shipment-goods",
      schema: {
        relationships: {
          adrs: {
            type: "domestic-road-shipment-good-adrs",
          },
        },
      },
    },
    { type: "domestic-road-shipment-good-adrs", schema: {} },
    { type: "domestic-road-shipment-activities", schema: {} },
    { type: "domestic-road-shipment-tracking-urls", schema: {} },
    {
      type: "international-road-shipment-goods",
      schema: {
        relationships: {
          adrs: {
            type: "international-road-shipment-good-adrs",
          },
        },
      },
    },
    { type: "international-road-shipment-good-adrs", schema: {} },
    { type: "international-road-shipment-activities", schema: {} },
    { type: "international-road-shipment-tracking-urls", schema: {} },
    { type: "notification-interactions", schema: {} },
    { type: "api-route-fields", schema: {} },
    {
      type: "contract-managers",
      schema: {
        relationships: {
          user: {
            type: "users",
          },
          company: {
            type: "companies",
          },
        },
      },
    },
    {
      type: "indoor-sales",
      schema: {
        relationships: {
          indoorUser: {
            type: "users",
          },
          outdoorUser: {
            type: "users",
          },
        },
      },
    },
    { type: "client-user-sessions", schema: {} },
    { type: "allowed-register-domains", schema: {} },
    { type: "company-business-line-details", schema: {} },
    { type: "company-contract-addendums", schema: {} },
    { type: "company-kpis", schema: {} },
    { type: "company-annexes", schema: {} },
    {
      type: "activity-statuses",
      schema: {
        relationships: {
          activityTypes: {
            type: "activity-types",
          },
        },
      },
    },
    {
      type: "activity-types",
      schema: {
        relationships: {
          activityStatus: {
            type: "activity-statuses",
          },
        },
      },
    },
    {
      type: "exchange-rates",
      schema: {
        relationships: {
          exchangeRatesChanges: {
            type: "exchange-rate-changes",
          },
        },
      },
    },
    { type: "exchange-rate-changes", schema: {} },
    { type: "company-for-alls", schema: {} },
    { type: "account-receivables", schema: {} },
    { type: "order-datas", schema: {} },
    {
      type: "strongo-shipments",
      schema: {
        relationships: {
          strongoShipmentParcels: {
            type: "strongo-shipment-parcels",
          },
          company: {
            type: "companies",
          },
          transportOrder: {
            type: "strongo-transport-orders",
          },
        },
      },
    },
    {
      type: "strongo-shipment-parcels",
      schema: {
        schema: {
          relationships: {
            strongoShipmentParcelHistories: {
              type: "strongo_shipment_parcel_histories",
            },
            strongoShipmentRelatedAwb: {
              type: "strongo_shipment_parcels",
            },
            strongoInvoice: {
              type: "strongo_invoices",
            },
            transportOrder: {
              type: "strongo-transport-orders",
            },
          },
        },
      },
    },
    { type: "strongo-shipment-parcel-histories", schema: {} },
    {
      type: "strongo-shipment-parcel-delivery-date-changes",
      schema: {
        relationships: {
          strongoShipmentParcel: {
            type: "strongo-shipment-parcels",
          },
        },
      },
    },
    {
      type: "strongo-invoices",
      schema: {
        relationships: {
          strongoShipment: {
            type: "strongo-shipments",
          },
          customerLedgerEntry: {
            type: "customer_ledger_entry",
          },
        },
      },
    },
    { type: "strongo-invoice-histories", schema: {} },
    { type: "dashboard-order-charts", schema: {} },
    { type: "krakend-endpoints", schema: {} },
    {
      type: "krakend-tokens",
      schema: {
        relationships: {
          endpoints: {
            type: "krakend-endpoints",
          },
          tokenDemand: {
            type: "krakend-token-demands",
          },
          generatedByUser: {
            type: "users",
          },
          requestedByUser: {
            type: "users",
          },
        },
      },
    },
    {
      type: "krakend-token-demands",
      schema: {
        relationships: {
          krakendToken: {
            type: "krakend-tokens",
          },
          user: {
            type: "users",
          },
          processedByUser: {
            type: "users",
          },
          endpoints: {
            type: "krakend-endpoints",
          },
        },
      },
    },
    {
      type: "mcp-tokens",
      schema: {
        relationships: {
          user: {
            type: "users",
          },
        },
      },
    },
    {
      type: "mcp-token-activities",
      schema: {
        relationships: {
          user: {
            type: "users",
          },
          mcpToken: {
            type: "mcp-tokens",
          },
        },
      },
    },
    {
      type: "client-mcp-tokens",
      schema: {
        relationships: {
          clientUser: {
            type: "client-users",
          },
        },
      },
    },
    {
      type: "client-mcp-token-activities",
      schema: {
        relationships: {
          clientUser: {
            type: "client-users",
          },
          mcpToken: {
            type: "client-mcp-tokens",
          },
        },
      },
    },
    {
      type: "oneclick-tokens",
      schema: {
        relationships: {
          tokenDemand: {
            type: "oneclick-token-demands",
          },
          user: {
            type: "users",
          },
          generatedByUser: {
            type: "users",
          },
        },
      },
    },
    {
      type: "oneclick-token-demands",
      schema: {
        relationships: {
          oneclickToken: {
            type: "oneclick-tokens",
          },
          user: {
            type: "users",
          },
          processedByUser: {
            type: "users",
          },
        },
      },
    },
    {
      type: "applications",
      schema: {
        relationships: {
          applicationDemand: {
            type: "application-demands",
          },
          generatedByUser: {
            type: "users",
          },
          requestedByUser: {
            type: "users",
          },
        },
      },
    },
    {
      type: "application-demands",
      schema: {
        relationships: {
          application: {
            type: "applications",
          },
          user: {
            type: "users",
          },
          processedByUser: {
            type: "users",
          },
        },
      },
    },
    { type: "tms-road-data", schema: {} },
    { type: "tms-sea-data", schema: {} },
    { type: "erp-data", schema: {} },
    { type: "strongo-data", schema: {} },
    { type: "user-sessions", schema: {} },
    {
      type: "customer-ledger-entry-reversals",
      schema: {
        relationships: {
          reversalInvoice: {
            type: "customer-ledger-entries",
          },
          originalInvoice: {
            type: "customer-ledger-entries",
          },
        },
      },
    },
    { type: "ai-agent-permissions", schema: {} },
    { type: "ai-agent-conversations", schema: {} },
    {
      type: "cleo-conversations",
      schema: {
        relationships: {
          messages: { type: "cleo-conversation-messages" },
        },
      },
    },
    {
      type: "cleo-conversation-messages",
      schema: {
        relationships: {
          conversation: { type: "cleo-conversations" },
          tools: { type: "cleo-conversation-message-tools" },
        },
      },
    },
    {
      type: "cleo-conversation-message-tools",
      schema: {
        relationships: {
          message: { type: "cleo-conversation-messages" },
        },
      },
    },
    {
      type: "client-cleo-conversations",
      schema: {
        relationships: {
          messages: { type: "client-cleo-conversation-messages" },
          company: { type: "companies" },
          clientUser: { type: "client-users" },
        },
      },
    },
    {
      type: "client-cleo-conversation-messages",
      schema: {
        relationships: {
          conversation: { type: "client-cleo-conversations" },
          tools: { type: "client-cleo-conversation-message-tools" },
        },
      },
    },
    {
      type: "client-cleo-conversation-message-tools",
      schema: {
        relationships: {
          message: { type: "client-cleo-conversation-messages" },
        },
      },
    },
    {
      type: "admin-documentation-titles",
      schema: {
        relationships: {
          subtitles: { type: "admin-documentation-subtitles" },
          texts: { type: "admin-documentation-texts" },
          images: { type: "admin-documentation-images" },
        },
      },
    },
    {
      type: "admin-documentation-subtitles",
      schema: {
        relationships: {
          texts: { type: "admin-documentation-texts" },
          images: { type: "admin-documentation-images" },
        },
      },
    },
    {
      type: "admin-documentation-texts",
      schema: {},
    },
    {
      type: "admin-documentation-images",
      schema: {},
    },
    {
      type: "client-documentation-titles",
      schema: {
        relationships: {
          parent: { type: "client-documentation-titles" },
          children: { type: "client-documentation-titles" },
          subtitles: { type: "client-documentation-subtitles" },
          texts: { type: "client-documentation-texts" },
          images: { type: "client-documentation-images" },
        },
      },
    },
    {
      type: "client-documentation-subtitles",
      schema: {
        relationships: {
          title: { type: "client-documentation-titles" },
          texts: { type: "client-documentation-texts" },
          images: { type: "client-documentation-images" },
        },
      },
    },
    {
      type: "client-documentation-texts",
      schema: {},
    },
    {
      type: "client-documentation-images",
      schema: {},
    },
    {
      type: "etransport-declarations",
      schema: {
        relationships: {
          etransportDeclarationGoods: { type: "etransport-declaration-goods" },
          etransportDeclarationDocuments: { type: "etransport-declaration-documents" },
          etransportSubmissions: { type: "etransport-submissions" },
          etransportVehicleChanges: { type: "etransport-vehicle-changes" },
        },
      },
    },
    { type: "etransport-declaration-goods", schema: {} },
    { type: "etransport-declaration-documents", schema: {} },
    { type: "etransport-submissions", schema: {} },
    { type: "etransport-vehicle-changes", schema: {} },
  ];

  json_api_types.forEach((typeData) => {
    Serializer.register(typeData.type, {
      ...typeData.schema,
      afterDeserialize: (data: any) => {
        data.id = parseInt(data.id);
        return data;
      },
    });
  });

  const jsonApiSerialize = <T>(type: string, data: T): JSONAPIDocument => {
    return Serializer.serialize(type, data);
  };

  const jsonApiDeserialize = <T>(type: string, data: JSONAPIDocument): T => {
    return Serializer.deserialize(type, data);
  };

  const jsonApiSerializeRelationToOne = <T>(
    type: string,
    data: Object,
  ): JSONAPIDocument => {
    return {
      data: {
        type: type,
        id: data.id.toString(),
      },
    };
  };

  const jsonApiSerializeRelationToMany = <T>(
    type: string,
    data: Array<Object>,
  ): JSONAPIDocument => {
    console.log(data, "serialize to many data");
    const relationData = [];
    data.forEach((el) => {
      relationData.push({
        type: type,
        id: el.id.toString(),
      });
    });
    return {
      data: relationData,
    };
  };

  function getItems<T, DT>(
    schema: string,
    type: { new (data: DT): T },
    relations: string | string[] | undefined = undefined,
    fields: { [key: string]: Array<string> } | undefined = undefined,
    filters: Array<QueryFilter> | undefined = undefined,
    pagination: JsonApiPagination | undefined = undefined,
    sort: string | string[] | undefined = undefined,
    cancelToken: CancelTokenSource | undefined = undefined,
    options: ApiCallOptions | undefined = undefined,
  ): Promise<T[]> {
    return new Promise((resolve, reject) => {
      let jsonApiUrl = `/${schema}`;
      let queryParams: Array<string> = [];

      if (relations !== undefined) {
        if (typeof relations === "string") {
          queryParams.push(`include=${relations}`);
        } else if (Array.isArray(relations)) {
          queryParams.push(`include=${relations.join(",")}`);
        }
      }
      if (fields !== undefined) {
        Object.keys(fields).forEach((relationName) => {
          queryParams.push(
            `fields[${relationName}]=${fields[relationName].join(",")}`,
          );
        });
      }

      if (Array.isArray(filters)) {
        filters.forEach((filter) => {
          if (filter.delimiter === null || filter.delimiter === undefined) {
            filter.values.forEach((filter_value) => {
              if (filter.parent !== undefined) {
                if (Array.isArray(filter.parent)) {
                  let parents = "";
                  filter.parent.forEach((parent) => {
                    parents += `[${parent}]`;
                  });

                  queryParams.push(
                    `filter${parents}[${filter.field}]=${encodeURIComponent(filter_value)}`,
                  );
                } else {
                  queryParams.push(
                    `filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter_value)}`,
                  );
                }
              } else {
                queryParams.push(
                  `filter[${filter.field}]=${encodeURIComponent(filter_value)}`,
                );
              }
            });
          } else {
            if (filter.parent !== undefined) {
              if (Array.isArray(filter.parent)) {
                let parents = "";
                filter.parent.forEach((parent) => {
                  parents += `[${parent}]`;
                });

                queryParams.push(
                  `filter${parents}[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
                );
              } else {
                queryParams.push(
                  `filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
                );
              }
            } else {
              queryParams.push(
                `filter[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
              );
            }
          }
        });
      }

      if (pagination !== undefined) {
        queryParams.push(`page[number]=${pagination.page_number}`);
        queryParams.push(`page[size]=${pagination.page_size}`);
      }

      if (sort !== undefined) {
        if (typeof sort === "string") {
          queryParams.push(`sort=${sort}`);
        } else if (Array.isArray(sort)) {
          queryParams.push(`sort=${sort.join(",")}`);
        }
      }

      if (queryParams.length > 0) {
        jsonApiUrl += `?${queryParams.join("&")}`;
      }

      axiosApi.value
        .get(jsonApiUrl, {
          cancelToken: cancelToken?.token,
        })
        .then((response) => {
          const data_items: DT[] = jsonApiDeserialize<DT[]>(
            schema,
            response.data,
          );
          const items: T[] = [];
          data_items.forEach((el) => items.push(new type(el)));
          if (response.data.meta) {
            meta.value = response.data.meta;
          } else {
            meta.value = undefined;
          }
          resolve(items);
        })
        .catch((error) => {
          console.log(error);
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          if (
            options === undefined ||
            options.ignore_errors === undefined ||
            options.ignore_errors === false
          ) {
            showError(`Get items failed`, message);
          }
          reject("An error has occurred");
        });
    });
  }

  function getItem<T, DT>(
    schema: string,
    id: number,
    type: { new (data: DT): T },
    relations: string | string[] | undefined = undefined,
    fields: { [key: string]: Array<string> } | undefined = undefined,
    filters: Array<QueryFilter> | undefined = undefined,
    pagination: JsonApiPagination | undefined = undefined,
    sort: string | string[] | undefined = undefined,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      let jsonApiUrl = `/${schema}/${id}`;
      let queryParams: Array<string> = [];

      if (relations !== undefined) {
        if (typeof relations === "string") {
          queryParams.push(`include=${relations}`);
        } else if (Array.isArray(relations)) {
          queryParams.push(`include=${relations.join(",")}`);
        }
      }
      if (fields !== undefined) {
        Object.keys(fields).forEach((relationName) => {
          queryParams.push(
            `fields[${relationName}]=${fields[relationName].join(",")}`,
          );
        });
      }

      if (Array.isArray(filters)) {
        filters.forEach((filter) => {
          if (filter.delimiter === null || filter.delimiter === undefined) {
            filter.values.forEach((filter_value) => {
              if (filter.parent !== undefined) {
                if (Array.isArray(filter.parent)) {
                  let parents = "";
                  filter.parent.forEach((parent) => {
                    parents += `[${parent}]`;
                  });

                  queryParams.push(
                    `filter${parents}[${filter.field}]=${encodeURIComponent(filter_value)}`,
                  );
                } else {
                  queryParams.push(
                    `filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter_value)}`,
                  );
                }
              } else {
                queryParams.push(
                  `filter[${filter.field}]=${encodeURIComponent(filter_value)}`,
                );
              }
            });
          } else {
            if (filter.parent !== undefined) {
              if (Array.isArray(filter.parent)) {
                let parents = "";
                filter.parent.forEach((parent) => {
                  parents += `[${parent}]`;
                });

                queryParams.push(
                  `filter${parents}[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
                );
              } else {
                queryParams.push(
                  `filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
                );
              }
            } else {
              queryParams.push(
                `filter[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
              );
            }
          }
        });
      }

      if (pagination !== undefined) {
        queryParams.push(`page[number]=${pagination.page_number}`);
        queryParams.push(`page[size]=${pagination.page_size}`);
      }

      if (sort !== undefined) {
        if (typeof sort === "string") {
          queryParams.push(`sort=${sort}`);
        } else if (Array.isArray(sort)) {
          queryParams.push(`sort=${sort.join(",")}`);
        }
      }

      if (queryParams.length > 0) {
        jsonApiUrl += `?${queryParams.join("&")}`;
      }

      axiosApi.value
        .get(jsonApiUrl)
        .then((response) => {
          // console.log(response,"vvvv")
          const data_item: DT = jsonApiDeserialize<DT>(schema, response.data);
          const item: T = new type(data_item);
          resolve(item);
          // const data_items: DT[] = jsonApiDeserialize<DT[]>(schema, response.data as JSONAPIDocument);
          // const items: T[] = [];
          // console.log("data_items",data_items)
          // console.log("items",items)
          // data_items.forEach(el => items.push(new type(el)));
          // console.log("aaaassd",response.data)
          // if (response.data.meta) {
          //     meta.value = response.data.meta;
          // } else {
          //     meta.value = undefined;
          // }
          // resolve(item);
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Get item failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function createItem<T, DT>(
    schema: string,
    data: T | any,
    type: { new (data: DT): T },
    extra: null | any,
    relations: string | string[] | undefined = undefined,
  ): Promise<T> {
    let extra_headers = {};

    if (extra) {
      extra_headers = extra;
    }

    return new Promise((resolve, reject) => {
      const payload: JSONAPIDocument = jsonApiSerialize<T>(schema, data);
      let jsonApiUrl = `/${schema}`;
      if (relations !== undefined) {
        if (typeof relations === "string") {
          jsonApiUrl += `?include=${relations}`;
        } else if (Array.isArray(relations)) {
          jsonApiUrl += `?include=${relations.join(",")}`;
        }
      }
      axiosApi.value
        .post(jsonApiUrl, payload, {
          headers: extra_headers,
        })
        .then((response) => {
          const data: DT = jsonApiDeserialize<DT>(
            schema,
            response.data as JSONAPIDocument,
          );
          const item: T = new type(data);
          resolve(item);
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Create item failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function saveItem<T, DT>(
    schema: string,
    id: number,
    data: T | any,
    type: { new (data: DT): T },
    relations: string | string[] | undefined = undefined,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const payload: JSONAPIDocument = jsonApiSerialize<T>(schema, data);
      let jsonApiUrl = `/${schema}/${id}`;
      if (relations !== undefined) {
        if (typeof relations === "string") {
          jsonApiUrl += `?include=${relations}`;
        } else if (Array.isArray(relations)) {
          jsonApiUrl += `?include=${relations.join(",")}`;
        }
      }
      axiosApi.value
        .patch(jsonApiUrl, payload)
        .then((response) => {
          const data: DT = jsonApiDeserialize<DT>(
            schema,
            response.data as JSONAPIDocument,
          );
          const item: T = new type(data);
          if (response.data.meta) {
            meta.value = response.data.meta;
          } else {
            meta.value = undefined;
          }
          resolve(item);
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Update item failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function deleteItem(schema: string, id: number): Promise<void> {
    return new Promise((resolve, reject) => {
      axiosApi.value
        .delete(`/${schema}/${id}`)
        .then((response) => {
          resolve();
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Delete item failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function relationDeleteItem<T, DT>(
    schema: string,
    relationship: string,
    parent_id: number,
    related_id: number,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const payload = {
        data: [
          {
            type: relationship,
            id: related_id.toString(),
          },
        ],
      };
      console.log("delete", payload);
      let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
      axiosApi.value
        .delete(jsonApiUrl, { data: payload })
        .then((response) => {
          console.log("iii", response);
          resolve();
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Relation delete failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function relationAddItem<T, DT>(
    schema: string,
    relationship: string,
    parent_id: number,
    data: any,
    type: {
      new (data: DT): T;
    },
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const payload: JSONAPIDocument = jsonApiSerializeRelationToOne<T>(
        relationship,
        data,
      );
      let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
      axiosApi.value
        .post(jsonApiUrl, payload)
        .then((response) => {
          const data: DT = jsonApiDeserialize<DT>(
            schema,
            response.data as JSONAPIDocument,
          );
          const item: T = new type(data);
          resolve(item);
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Relation add item failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function relationAddItems<T, DT>(
    schema: string,
    relationship: string,
    parent_id: number,
    data: any,
    type: {
      new (data: DT): T;
    },
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const payload: JSONAPIDocument = jsonApiSerializeRelationToMany<T>(
        relationship,
        data,
      );
      let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
      axiosApi.value
        .post(jsonApiUrl, payload)
        .then((response) => {
          const data: DT = jsonApiDeserialize<DT>(
            schema,
            response.data as JSONAPIDocument,
          );
          const item: T = new type(data);
          resolve(item);
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Relation add items failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function relationReplaceItem<T, DT>(
    schema: string,
    relationship: string,
    parent_id: number,
    data: any,
    type: {
      new (data: DT): T;
    },
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const payload: JSONAPIDocument = jsonApiSerializeRelationToOne<T>(
        relationship,
        data,
      );
      let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
      axiosApi.value
        .patch(jsonApiUrl, payload)
        .then((response) => {
          const data: DT = jsonApiDeserialize<DT>(
            schema,
            response.data as JSONAPIDocument,
          );
          const item: T = new type(data);
          resolve(item);
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Relation replace failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function relationReplaceItems<T, DT>(
    schema: string,
    relationship: string,
    parent_id: number,
    data: any,
    type: {
      new (data: DT): T;
    },
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const payload: JSONAPIDocument = jsonApiSerializeRelationToMany<T>(
        relationship,
        data,
      );
      let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
      axiosApi.value
        .patch(jsonApiUrl, payload)
        .then((response) => {
          const data: DT = jsonApiDeserialize<DT>(
            schema,
            response.data as JSONAPIDocument,
          );
          const item: T = new type(data);
          resolve(item);
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Relation replace items failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function relationGetItem<T, DT>(
    schema: string,
    relationship: string,
    parent_id: number,
    type: {
      new (data: DT): T;
    },
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
      axiosApi.value
        .get(jsonApiUrl)
        .then((response) => {
          const data_item: DT = jsonApiDeserialize<DT>(
            schema,
            response.data as JSONAPIDocument,
          );
          const item: T = new type(data_item);
          resolve(item);
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Relation get item failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function relationGetItems<T, DT>(
    schema: string,
    relationship: string,
    parent_id: number,
    type: { new (data: DT): T },
    relations: string | string[] | undefined = undefined,
    fields: { [key: string]: Array<string> } | undefined = undefined,
    filters: Array<QueryFilter> | undefined = undefined,
    pagination: JsonApiPagination | undefined = undefined,
    sort: string | string[] | undefined = undefined,
  ): Promise<T[]> {
    return new Promise((resolve, reject) => {
      let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
      let queryParams: Array<string> = [];

      if (relations !== undefined) {
        if (typeof relations === "string") {
          queryParams.push(`include=${relations}`);
        } else if (Array.isArray(relations)) {
          queryParams.push(`include=${relations.join(",")}`);
        }
      }
      if (fields !== undefined) {
        Object.keys(fields).forEach((relationName) => {
          queryParams.push(
            `fields[${relationName}]=${fields[relationName].join(",")}`,
          );
        });
      }

      if (Array.isArray(filters)) {
        filters.forEach((filter) => {
          if (filter.delimiter === null || filter.delimiter === undefined) {
            filter.values.forEach((filter_value) => {
              if (filter.parent !== undefined) {
                if (Array.isArray(filter.parent)) {
                  let parents = "";
                  filter.parent.forEach((parent) => {
                    parents += `[${parent}]`;
                  });

                  queryParams.push(
                    `filter${parents}[${filter.field}]=${encodeURIComponent(filter_value)}`,
                  );
                } else {
                  queryParams.push(
                    `filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter_value)}`,
                  );
                }
              } else {
                queryParams.push(
                  `filter[${filter.field}]=${encodeURIComponent(filter_value)}`,
                );
              }
            });
          } else {
            if (filter.parent !== undefined) {
              if (Array.isArray(filter.parent)) {
                let parents = "";
                filter.parent.forEach((parent) => {
                  parents += `[${parent}]`;
                });

                queryParams.push(
                  `filter${parents}[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
                );
              } else {
                queryParams.push(
                  `filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
                );
              }
            } else {
              queryParams.push(
                `filter[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
              );
            }
          }
        });
      }

      if (pagination !== undefined) {
        queryParams.push(`page[number]=${pagination.page_number}`);
        queryParams.push(`page[size]=${pagination.page_size}`);
      }

      if (sort !== undefined) {
        if (typeof sort === "string") {
          queryParams.push(`sort=${sort}`);
        } else if (Array.isArray(sort)) {
          queryParams.push(`sort=${sort.join(",")}`);
        }
      }

      if (queryParams.length > 0) {
        jsonApiUrl += `?${queryParams.join("&")}`;
      }

      axiosApi.value
        .get(jsonApiUrl)
        .then((response) => {
          const data_items: DT[] = jsonApiDeserialize<DT[]>(
            relationship,
            response.data as JSONAPIDocument,
          );
          const items: T[] = [];
          // console.log("aaaa1", response)
          // console.log("aaaa2", data_items)

          if (relations !== undefined) {
            response.data?.data?.forEach((el) => {
              const includedResource = response.data?.included?.find(
                (includedEl) =>
                  el.id === includedEl.id && el.type === includedEl.type,
              );

              if (includedResource !== undefined) {
                const newItem = new type(includedResource.attributes);
                newItem.id = parseInt(includedResource.id);
                items.push(newItem);
              }
            });
            resolve(items);
          } else {
            resolve(data_items);
          }

          if (response.data.meta) {
            meta.value = response.data.meta;
          } else {
            meta.value = undefined;
          }
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let message = `${schema} error - `;
          if (error.response?.data?.errors?.length > 0) {
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(`Relation get items failed`, message);
          reject("An error has occurred");
        });
    });
  }

  function getCustomAction<T, DT>(
    schema: string,
    parent_id: number | null,
    action: string,
    type: {
      new (data: DT): T | undefined;
    } = undefined,
    params: Array<{ name: string; value: string }> | undefined = undefined,
    filters: Array<QueryFilter> | undefined = undefined,
    cancelToken: CancelTokenSource | undefined = undefined,
  ): Promise<T | any> {
    return new Promise((resolve, reject): any => {
      let jsonApiUrl = `/${schema}/`;
      let queryParams: Array<string> = [];

      if (parent_id !== null) {
        jsonApiUrl += `${parent_id}/`;
      }

      jsonApiUrl += `-actions/${action}`;

      if (Array.isArray(params)) {
        params.forEach((param) => {
          queryParams.push(`${param.name}=${param.value}`);
        });
      }

      if (Array.isArray(filters)) {
        filters.forEach((filter) => {
          if (filter.delimiter === null || filter.delimiter === undefined) {
            filter.values.forEach((filter_value) => {
              if (filter.parent !== undefined) {
                if (Array.isArray(filter.parent)) {
                  let parents = "";
                  filter.parent.forEach((parent) => {
                    parents += `[${parent}]`;
                  });

                  queryParams.push(
                    `filter${parents}[${filter.field}]=${encodeURIComponent(filter_value)}`,
                  );
                } else {
                  queryParams.push(
                    `filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter_value)}`,
                  );
                }
              } else {
                queryParams.push(
                  `filter[${filter.field}]=${encodeURIComponent(filter_value)}`,
                );
              }
            });
          } else {
            if (filter.parent !== undefined) {
              if (Array.isArray(filter.parent)) {
                let parents = "";
                filter.parent.forEach((parent) => {
                  parents += `[${parent}]`;
                });

                queryParams.push(
                  `filter${parents}[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
                );
              } else {
                queryParams.push(
                  `filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
                );
              }
            } else {
              queryParams.push(
                `filter[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`,
              );
            }
          }
        });
      }

      if (queryParams.length > 0) {
        jsonApiUrl += `?${queryParams.join("&")}`;
      }

      axiosApi.value
        .get(jsonApiUrl, {
          cancelToken: cancelToken?.token,
        })
        .then((response) => {
          if (type !== undefined) {
            const data: any = jsonApiDeserialize<any>(
              schema,
              response.data as JSONAPIDocument,
            );
            if (Array.isArray(data)) {
              const items: T[] = data.map((el: DT) => new type(el));
              resolve(items);
            } else {
              const item: T = new type(data as DT);
              resolve(item);
            }
          } else {
            resolve(response.data);
          }
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let title = `Get custom action failed`;
          let message = `${schema}/${action} error - `;
          if (error.response?.data?.errors?.length > 0) {
            title = error.response.data.errors[0].title
              ? error.response.data.errors[0].title
              : title;
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          if (authStore.isLoggedIn) {
            showError(title, message);
          }
          reject("An error has occurred");
        });
    });
  }

  function postCustomAction<T, DT>(
    schema: string,
    parent_id: number | null,
    action: string,
    payload: any,
    type: { new (data: DT): T | undefined } = undefined,
    options: ApiCallOptions | undefined = undefined,
  ): Promise<T | any> {
    return new Promise((resolve, reject): any => {
      let jsonApiUrl = `/${schema}/`;
      if (parent_id !== null) {
        jsonApiUrl += `${parent_id}/`;
      }

      jsonApiUrl += `-actions/${action}`;

      let call_data: JSONAPIDocument = jsonApiSerialize(schema, payload, true);

      if (options && options.serialize === false) {
        call_data = payload;
      }

      axiosApi.value
        .post(jsonApiUrl, call_data)
        .then((response) => {
          // meta.value = response.data.meta;
          if (type !== undefined) {
            const data: DT = jsonApiDeserialize<DT>(
              schema,
              response.data as JSONAPIDocument,
            );
            const item: T = new type(data);
            resolve(item);
          } else {
            resolve(response.data);
          }
        })
        .catch((error) => {
          if (error?.code === "ERR_CANCELED") {
            reject("Call cancelled");
            return;
          }
          let title = `Post custom action failed`;
          let message = `${schema}/${action} error - `;
          if (error.response?.data?.errors?.length > 0) {
            console.log(
              error.response?.data?.errors[0],
              "error.response?.data?.errors[0]",
            );
            title = error.response.data.errors[0].title
              ? error.response.data.errors[0].title
              : title;
            error.response.data.errors.forEach((err) => {
              message += err.detail + "<br>";
            });
          }
          console.log(error);
          showError(title, message);
          reject("An error has occurred");
        });
    });
  }

  return {
    BASE_URL,
    PARALLEL_REQUESTS,
    axiosApi,
    axiosAuth,
    jsonApiSerialize,
    jsonApiDeserialize,
    meta,
    getItems,
    getItem,
    createItem,
    saveItem,
    relationDeleteItem,
    deleteItem,
    relationAddItem,
    relationAddItems,
    relationReplaceItem,
    relationReplaceItems,
    relationGetItem,
    relationGetItems,
    getCustomAction,
    postCustomAction,
    CURRENT_LANGUAGE,
    CancelToken,
  };
});
